import React, { useState, useCallback, useEffect } from 'react';

import { useDictionary, usePage } from '@/src/app/hooks';
import {
    Loading,
    ErrorMessage,
    ResourceBoxInner,
    Container,
    type ResourceHeaderDTO,
} from '@/src/ui';
import { apiRequest } from '@/src/services';
import './ProfessionalLearningPage.css';

const ProfessionalLearningPage: React.FC = () => {
    const { translate } = useDictionary();
    const { setSubheaderTitle, setSubheaderType } = usePage();
    document.title = translate('Professional Learning', 'Professional Learning');

    const [professionalLearningData, setProfessionalLearningData] = useState<
        ResourceHeaderDTO[] | null
    >(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setSubheaderTitle(translate('Professional Learning'));
        setSubheaderType('default');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translate]);

    const fetchData = useCallback(async () => {
        setError('');
        setLoading(true);
        setProfessionalLearningData(null);
        try {
            const res = await apiRequest<ResourceHeaderDTO[]>('GET', `dbo/professionalLearning`);
            setProfessionalLearningData(res.data);
        } catch (error: any) {
            if (error.status === 403) {
                setError('You do not have permission to view this page.');
            } else if (error.status === 404) {
                setError('Professional Learning data could not be found at this time.');
            } else {
                setError('Failed to fetch Professional Learning data.');
            }
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        (async function init() {
            fetchData();
        })();
    }, [fetchData]);

    const renderColumn = useCallback(
        (colNo: number) => {
            const boxes =
                professionalLearningData?.filter((box) => box.columnNumber === colNo) || [];
            return (
                <>
                    {boxes.map((box) => (
                        <div
                            id={box.id}
                            className={`professional-learning-box background pd-primary`}
                        >
                            <div className="tile-headings">{box.title}</div>
                            {box.items.map((item) => {
                                const anchorId = `professional-learning-container-c${colNo}-${item.id}`;
                                return (
                                    <ResourceBoxInner
                                        key={anchorId}
                                        id={anchorId}
                                        item={item}
                                        namespace="professional-learning"
                                    />
                                );
                            })}
                        </div>
                    ))}
                </>
            );
        },
        [professionalLearningData]
    );

    return (
        <Container>
            <ErrorMessage error={error} />
            {loading ? (
                <Loading />
            ) : (
                <div className="professional-learning-container">
                    <div className="row gx-5 justify-content-center" id="dashboard">
                        {[1, 2, 3].map((num) => {
                            const hasData = professionalLearningData?.some(
                                (box) => box.columnNumber === num
                            );

                            return hasData ? (
                                <div
                                    key={num}
                                    id={`column_${num}`}
                                    className="col-4 professional-learning-column"
                                >
                                    {renderColumn(num)}
                                </div>
                            ) : null;
                        })}
                    </div>
                </div>
            )}
        </Container>
    );
};

export default ProfessionalLearningPage;
